<template>
  <v-navigation-drawer
    id="mobile-navigation-menu"
    :model-value="modelValue"
    @update:modelValue="(v: any) => $emit('update:modelValue', v)"
    floating
    temporary
    location="right"
    class="hidden-lg-and-up"
    :class="
      $vuetify.display.width < 480 ? 'mobile-navigation-menuPhone' : 'mobile-navigation-menuTablet'
    "
  >
    <v-list class="pa-1" role="list">
      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          nav.goToMyFinancialHome();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title
          :class="[
            nav.isRouteDashboard() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
            isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
          ]"
          >{{ $t('L_home') }}</v-list-item-title
        >
      </v-list-item>
      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          nav.termLifeAction();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title
          :class="[
            nav.isRouteTermLifeBenefits() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
            isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
          ]"
          >{{ $t('L_life_insurance') }}</v-list-item-title
        >
      </v-list-item>
      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          nav.investmentsAction();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title>
          <span
            :class="[
              !hasInvestments ? 'not-live' : '',
              nav.isRouteInvestments() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
              isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
            ]"
            >{{ $t('L_investment') }}
          </span>
        </v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          nav.debtAction();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title>
          <span
            :class="[
              !nav.hasActiveDebt() ? 'not-live' : '',
              nav.isRouteDebtSolutions() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
              isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
            ]"
            >{{ $t('L_debt') }}</span
          >
        </v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="showRepLink && !isHideAllButLocale"
        @click.prevent="
          nav.repAction();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title
          :class="[
            nav.isRouteMyRep() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
            isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
          ]"
          >{{ $t('L_my_rep') }}</v-list-item-title
        >
      </v-list-item>
      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          nav.documentsAction($vuetify.display.xs);
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title
          :class="[
            nav.isRouteDocuments() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
            isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
          ]"
          >{{ $t('L_my_document') }}</v-list-item-title
        >
      </v-list-item>

      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          helpAction();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title :class="[isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14']">{{
          $t('L_help')
        }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!isHideAllButLocale"
        @click.prevent="
          nav.accountAction();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title
          :class="[
            nav.isRouteMyAccount() ? 'selectedMenuItemClass' : 'nonSelectedMenuItemClass',
            isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14',
          ]"
          >{{ $t('L_account_setting') }}</v-list-item-title
        >
      </v-list-item>
      <v-list-item
        role="listitem"
        @click="
          logout();
          clickToCloseTheDrawer();
        "
      >
        <v-list-item-title :class="[isWideEnoughForFullMenu ? 'font-size--16' : 'font-size--14']">{{
          $t('L_sign_out')
        }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { getNav } from '@/shared/navLogic';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { mapStores, mapState } from 'pinia';
import { type ComponentOptions, defineComponent } from 'vue';
import { COUNTRIES } from '@/shared/models/constants.models';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';

const componentOptions: ComponentOptions = {
  name: 'NavigationDrawer',
  data: () => ({
    nav: getNav(),
    locales: [],
  }),
  mixins: [openInNewWindow],
  emits: ['update:modelValue', 'logout'],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    isHideAllButLocale: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    ...mapStores(useGlobalStore, useCustomerStore),
    isWideEnoughForFullMenu() {
      return this.$vuetify.display.width > 1284;
    },
    helpEnabled() {
      return window.appConfig.help.enabled;
    },
    hasInvestments() {
      return this.customerStore.investments.summary.totalValue > 0;
    },
    showRepLink() {
      return this.globalStore.agents?.length > 0;
    },
    isCanadian() {
      return this.customerProfile?.country === COUNTRIES.CANADA;
    },
  },
  mounted() {
    if (this.isCanadian) {
      this.locales.push({ title: 'English (CAN)', value: 'en-CA' });
      this.locales.push({ title: 'French (CAN)', value: 'fr-CA' });
    } else {
      this.locales.push({ title: 'English (US)', value: 'en-US' });
      this.locales.push({ title: 'Español (US)', value: 'es-US' });
    }
  },
  methods: {
    clickToCloseTheDrawer() {
      this.$emit('update:modelValue', false);
    },
    async logout() {
      await this.$emit('logout');
    },
    async helpAction() {
      const globalStore = useGlobalStore();
      if (globalStore.containsFeature('HELP')) {
        const newRightAnswersUrl =
          this.locale === 'fr_CA'
            ? window.appConfig.endpoints.frRightanswerslinkpostjune2025
            : window.appConfig.endpoints.rightanswerslinkpostjune2025;
        const isWeb = CapacitorFacade.isWeb();
        await this.openInNewWindow({
          url: newRightAnswersUrl,
          inappbrowser: !isWeb,
          target: 'RADashboardWinName',
        });
      }
    },
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
#mobile-navigation-menu {
  z-index: 110;
  margin-top: 64px !important;
}

.mobile-navigation-menuPhone {
  z-index: 110;
  width: 180px;
  max-width: 180px;
}

.mobile-navigation-menuTablet {
  z-index: 110;
  width: 240px;
  max-width: 240px;
}

.languageSelectorMobile {
  position: absolute;
  left: 60px;
  top: 10px;
}
</style>
